"use client";

import axo from "@/lib/axo";
import { errorsToObject } from "@/lib/helpers";
import type { FormikProps } from "formik";
import isEmpty from "lodash/isEmpty";
import useUtmParams from "./useUtmParams";

type HttpMethod = "get" | "post" | "put" | "patch" | "delete";

interface ApiConfig<TData = any, TResponse = any> {
  endpoint?: string;
  method?: HttpMethod;
  headers?: Record<string, string>;
  defaultData?: Partial<TData>;
  params?: Record<string, any>;
  withCredentials?: boolean;
  preventFormReset?: boolean;
  transformRequest?: (data: TData) => any;
  transformResponse?: (response: any) => TResponse;
  onSuccess?: (response: TResponse, formikProps?: FormikProps<TData>) => void;
  onError?: (error: any, formikProps?: FormikProps<TData>) => void;
  validateStatus?: (status: number) => boolean;
}

const useApiSubmit = <TData = any, TResponse = any>(
  config: ApiConfig<TData, TResponse>
) => {
  const utmParams = useUtmParams();
  const axoConfig = {
    method: config.method || "post",
    url: config.endpoint || "/enquiry-details",
    headers: {
      "Content-Type": "application/json",
      ...config.headers,
    },
    withCredentials: config.withCredentials,
    params: config.params || {},
  };

  const handleSubmit = async (
    data: TData,
    formikProps?: FormikProps<TData>
  ) => {
    try {
      const transformedData = config.transformRequest
        ? config.transformRequest(data)
        : data;
      const resp = await axo({
        ...axoConfig,
        data: {
          ...config.defaultData,
          ...transformedData,
          ...utmParams,
          clientEnquirySource:
            typeof window !== "undefined" ? window.location.href : "",
          terms: transformedData?.terms == true ? 1 : 0,
        },
      });

      const isValidStatus = config.validateStatus
        ? config.validateStatus(resp.status)
        : resp.status >= 200 && resp.status < 300;

      if (isValidStatus) {
        if (!config.preventFormReset) {
          formikProps?.resetForm();
        }
        const transformedResponse = config.transformResponse
          ? config.transformResponse(resp.data)
          : resp.data;

        config.onSuccess?.(
          { response: transformedResponse, ...data } as TResponse,
          formikProps
        );
        return transformedResponse;
      }
    } catch (error: any) {
      if (error.response) {
        const { data: errorData } = error.response;

        if (error.response.status === 422 && !isEmpty(errorData.errors)) {
          const modifiedErrors = errorsToObject(errorData.errors);
          formikProps?.setErrors(modifiedErrors);
        }

        config.onError?.(error, formikProps);
      }
      throw error;
    }
  };

  return {
    handleSubmit,
    axoConfig,
  };
};

export default useApiSubmit;
